export type TerminationType =
  | "without_cause_university"
  | "with_cause"
  | "coach_initiated";

export type BuyoutFormula =
  | "schedule"
  | "remaining_salary"
  | "fixed"
  | "none";

export interface BuyoutScheduleEntry {
  year: number;
  amount: number;
}

export interface TerminationRules {
  withoutCauseUniversity: BuyoutFormula;
  withCause: BuyoutFormula;
  coachInitiated: BuyoutFormula;
}

export const DEFAULT_TERMINATION_RULES: TerminationRules = {
  withoutCauseUniversity: "schedule",
  withCause: "none",
  coachInitiated: "none",
};

export interface BuyoutInputs {
  startDate: string;
  endDate: string;
  annualSalary: number;
  buyoutClause: number | null;
  buyoutSchedule: BuyoutScheduleEntry[];
  terminationRules: TerminationRules;
  terminationDate: string;
  terminationType: TerminationType;
  mitigationEnabled: boolean;
  mitigationAmount: number;
}

export interface BuyoutBreakdown {
  terminationDate: string;
  terminationType: TerminationType;
  formulaApplied: BuyoutFormula;
  contractYear: number;
  remainingMonths: number;
  remainingSalary: number;
  scheduledAmount: number | null;
  rawAmountOwed: number;
  mitigationOffset: number;
  finalAmountOwed: number;
  explanation: string;
  warnings: string[];
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function parseISODate(s: string): Date {
  const d = new Date(`${s}T00:00:00Z`);
  if (isNaN(d.getTime())) {
    throw new Error(`Invalid date: ${s}`);
  }
  return d;
}

function monthsBetween(from: Date, to: Date): number {
  if (to.getTime() <= from.getTime()) return 0;
  const days = (to.getTime() - from.getTime()) / MS_PER_DAY;
  return days / (365.25 / 12);
}

export function getContractYear(startDate: string, terminationDate: string): number {
  const start = parseISODate(startDate);
  const term = parseISODate(terminationDate);
  if (term.getTime() < start.getTime()) return 1;
  const days = (term.getTime() - start.getTime()) / MS_PER_DAY;
  return Math.floor(days / 365.25) + 1;
}

export function getScheduledAmount(
  schedule: BuyoutScheduleEntry[],
  year: number,
): number | null {
  if (!schedule || schedule.length === 0) return null;
  const exact = schedule.find((e) => e.year === year);
  if (exact) return exact.amount;
  const sorted = [...schedule].sort((a, b) => a.year - b.year);
  let last: BuyoutScheduleEntry | null = null;
  for (const e of sorted) {
    if (e.year <= year) last = e;
  }
  if (last) return last.amount;
  return sorted[0].amount;
}

function ruleFor(rules: TerminationRules, type: TerminationType): BuyoutFormula {
  switch (type) {
    case "without_cause_university":
      return rules.withoutCauseUniversity;
    case "with_cause":
      return rules.withCause;
    case "coach_initiated":
      return rules.coachInitiated;
  }
}

function formatMoney(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

function terminationLabel(t: TerminationType): string {
  return {
    without_cause_university: "Without Cause (by University)",
    with_cause: "With Cause",
    coach_initiated: "Coach-Initiated Departure",
  }[t];
}

export function calculateBuyout(input: BuyoutInputs): BuyoutBreakdown {
  const warnings: string[] = [];
  const start = parseISODate(input.startDate);
  const end = parseISODate(input.endDate);
  const term = parseISODate(input.terminationDate);

  let remainingMonths = monthsBetween(term, end);
  if (term.getTime() < start.getTime()) {
    warnings.push("Termination date is before contract start date.");
    remainingMonths = monthsBetween(start, end);
  }
  if (term.getTime() > end.getTime()) {
    warnings.push("Termination date is after the contract end date — no remaining term.");
    remainingMonths = 0;
  }

  const monthlyRate = input.annualSalary / 12;
  const remainingSalary = monthlyRate * remainingMonths;
  const contractYear = getContractYear(input.startDate, input.terminationDate);
  const scheduledAmount = getScheduledAmount(input.buyoutSchedule, contractYear);

  const formula = ruleFor(input.terminationRules, input.terminationType);

  let rawAmountOwed = 0;
  let formulaExplanation = "";
  switch (formula) {
    case "schedule":
      if (scheduledAmount != null) {
        rawAmountOwed = scheduledAmount;
        formulaExplanation = `Year ${contractYear} buyout schedule: ${formatMoney(scheduledAmount)}.`;
      } else if (input.buyoutClause != null) {
        rawAmountOwed = input.buyoutClause;
        formulaExplanation = `No yearly schedule entered; falling back to flat buyout clause of ${formatMoney(input.buyoutClause)}.`;
        warnings.push("No buyout schedule defined for this year — used the flat buyout clause instead.");
      } else {
        rawAmountOwed = remainingSalary;
        formulaExplanation = `No schedule or flat buyout defined; falling back to remaining base salary of ${formatMoney(remainingSalary)}.`;
        warnings.push("No buyout schedule or flat buyout clause defined — used remaining base salary as a fallback.");
      }
      break;
    case "remaining_salary":
      rawAmountOwed = remainingSalary;
      formulaExplanation = `Remaining base salary owed for the balance of the term: ${formatMoney(remainingSalary)}.`;
      break;
    case "fixed":
      rawAmountOwed = input.buyoutClause ?? 0;
      formulaExplanation = input.buyoutClause != null
        ? `Flat buyout clause: ${formatMoney(input.buyoutClause)}.`
        : "Flat buyout clause was not set; treated as $0.";
      if (input.buyoutClause == null) warnings.push("Flat buyout clause is not set.");
      break;
    case "none":
      rawAmountOwed = 0;
      formulaExplanation = `No payment is owed for a ${terminationLabel(input.terminationType)} under the configured rules.`;
      break;
  }

  const mitigationOffset = input.mitigationEnabled
    ? Math.max(0, Math.min(input.mitigationAmount || 0, rawAmountOwed))
    : 0;

  const finalAmountOwed = Math.max(0, rawAmountOwed - mitigationOffset);

  const parts = [
    `Termination type: ${terminationLabel(input.terminationType)}.`,
    formulaExplanation,
  ];
  if (input.mitigationEnabled) {
    parts.push(
      `Mitigation offset of ${formatMoney(mitigationOffset)} applied (assumed future earnings the coach mitigates).`,
    );
  }
  parts.push(`Final estimated amount owed: ${formatMoney(finalAmountOwed)}.`);

  return {
    terminationDate: input.terminationDate,
    terminationType: input.terminationType,
    formulaApplied: formula,
    contractYear,
    remainingMonths: Math.round(remainingMonths * 100) / 100,
    remainingSalary: Math.round(remainingSalary * 100) / 100,
    scheduledAmount,
    rawAmountOwed: Math.round(rawAmountOwed * 100) / 100,
    mitigationOffset: Math.round(mitigationOffset * 100) / 100,
    finalAmountOwed: Math.round(finalAmountOwed * 100) / 100,
    explanation: parts.join(" "),
    warnings,
  };
}
